import React from "react";
import { motion } from "framer-motion";
import useAuth from "../useAuth";
import {
  LogOut,
  User,
  Mail,
  Phone,
  Shield,
  Edit,
  MapPin,
  Calendar,
  Lock,
  Camera,
} from "lucide-react";

export const ProfilePage = () => {
  const { user, logout } = useAuth();

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      logout();
    }
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case "admin":
        return "bg-purple-500/10 text-purple-400 border-purple-500/20";
      case "editor":
        return "bg-blue-500/10 text-blue-400 border-blue-500/20";
      case "teacher":
        return "bg-emerald-500/10 text-emerald-400 border-emerald-500/20";
      default:
        return "bg-gray-500/10 text-gray-400 border-gray-500/20";
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 p-6">
      <motion.div
        className="max-w-6xl mx-auto space-y-6"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <div className="p-3 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-2xl shadow-lg shadow-blue-500/20">
            <User className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-br from-blue-400 to-indigo-400 bg-clip-text text-transparent">
              My Profile
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              Manage your personal information and account settings
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column: Profile Card */}
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-gray-800 rounded-2xl border border-gray-700 overflow-hidden shadow-xl relative">
              <div className="h-32 bg-gradient-to-r from-blue-600 to-indigo-600"></div>
              <div className="px-6 pb-6 mt-[-50px] text-center">
                <div className="relative inline-block">
                  <div className="w-28 h-28 rounded-full border-4 border-gray-800 bg-gray-700 flex items-center justify-center text-4xl font-bold text-white overflow-hidden">
                    {user?.userPic ? (
                      <img
                        src={user.userPic}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="bg-gradient-to-br from-blue-500 to-purple-600 w-full h-full flex items-center justify-center">
                        {user?.fullName?.charAt(0) || "U"}
                      </span>
                    )}
                  </div>
                  <button className="absolute bottom-1 right-1 p-2 bg-indigo-600 rounded-full text-white hover:bg-indigo-700 transition-colors border-2 border-gray-800">
                    <Camera className="w-4 h-4" />
                  </button>
                </div>

                <h2 className="text-2xl font-bold text-white mt-4">
                  {user?.fullName || "Admin User"}
                </h2>
                <p className="text-gray-400 text-sm mb-4">
                  {user?.email || "admin@setu.com"}
                </p>

                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium border ${getRoleColor(
                    user?.userType || "admin"
                  )}`}
                >
                  <Shield className="w-3 h-3" />
                  {(user?.userType || "Administrator").toUpperCase()}
                </span>

                <div className="mt-8 grid grid-cols-2 gap-4 border-t border-gray-700 pt-6">
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-white">24</h3>
                    <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">
                      Projects
                    </p>
                  </div>
                  <div className="text-center">
                    <h3 className="text-2xl font-bold text-white">142</h3>
                    <p className="text-xs text-gray-400 uppercase tracking-widest mt-1">
                      Tasks
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700">
              <h3 className="text-white font-semibold mb-4">Account Actions</h3>
              <div className="space-y-3">
                <button className="w-full flex items-center justify-between p-3 rounded-xl bg-gray-700/50 hover:bg-gray-700 text-gray-300 hover:text-white transition-all group">
                  <span className="flex items-center gap-3">
                    <Edit className="w-4 h-4 text-blue-400" />
                    Edit Profile
                  </span>
                  <div className="w-6 h-6 rounded-full bg-gray-600 text-gray-400 flex items-center justify-center text-xs group-hover:bg-blue-600 group-hover:text-white transition-colors">
                    →
                  </div>
                </button>
                <button className="w-full flex items-center justify-between p-3 rounded-xl bg-gray-700/50 hover:bg-gray-700 text-gray-300 hover:text-white transition-all group">
                  <span className="flex items-center gap-3">
                    <Lock className="w-4 h-4 text-orange-400" />
                    Change Password
                  </span>
                  <div className="w-6 h-6 rounded-full bg-gray-600 text-gray-400 flex items-center justify-center text-xs group-hover:bg-orange-600 group-hover:text-white transition-colors">
                    →
                  </div>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-between p-3 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 transition-all group border border-red-500/20"
                >
                  <span className="flex items-center gap-3">
                    <LogOut className="w-4 h-4" />
                    Logout
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Right Column: Details */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-bold text-white flex items-center gap-2">
                  <User className="w-5 h-5 text-indigo-500" />
                  Personal Information
                </h3>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <div className="space-y-2">
                  <label className="text-gray-400 text-sm">Full Name</label>
                  <div className="flex items-center gap-3 p-3 bg-gray-900 rounded-xl border border-gray-700 text-white">
                    <User className="w-5 h-5 text-gray-500" />
                    {user?.fullName || "Admin User"}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-gray-400 text-sm">Email Address</label>
                  <div className="flex items-center gap-3 p-3 bg-gray-900 rounded-xl border border-gray-700 text-white">
                    <Mail className="w-5 h-5 text-gray-500" />
                    {user?.email || "admin@setu.com"}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-gray-400 text-sm">Phone Number</label>
                  <div className="flex items-center gap-3 p-3 bg-gray-900 rounded-xl border border-gray-700 text-white">
                    <Phone className="w-5 h-5 text-gray-500" />
                    {user?.mobileNo || "+91 98765 43210"}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-gray-400 text-sm">Role</label>
                  <div className="flex items-center gap-3 p-3 bg-gray-900 rounded-xl border border-gray-700 text-white capitalize">
                    <Shield className="w-5 h-5 text-gray-500" />
                    {user?.userType || "Admin"}
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-gray-400 text-sm">Location</label>
                  <div className="flex items-center gap-3 p-3 bg-gray-900 rounded-xl border border-gray-700 text-white">
                    <MapPin className="w-5 h-5 text-gray-500" />
                    Ranchi, India
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-gray-400 text-sm">Member Since</label>
                  <div className="flex items-center gap-3 p-3 bg-gray-900 rounded-xl border border-gray-700 text-white">
                    <Calendar className="w-5 h-5 text-gray-500" />
                    {user?.createdAt
                      ? new Date(user.createdAt).toLocaleDateString()
                      : "Jan 2024"}
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
              <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2">
                <Shield className="w-5 h-5 text-indigo-500" />
                Recent Activity
              </h3>
              <div className="space-y-6">
                {[
                  {
                    action: "Updated profile picture",
                    time: "2 hours ago",
                    icon: <User className="w-4 h-4 text-blue-400" />,
                    color: "bg-blue-500/10",
                  },
                  {
                    action: "Changed password",
                    time: "1 day ago",
                    icon: <Lock className="w-4 h-4 text-orange-400" />,
                    color: "bg-orange-500/10",
                  },
                  {
                    action: "Logged in from new device",
                    time: "3 days ago",
                    icon: <Shield className="w-4 h-4 text-green-400" />,
                    color: "bg-green-500/10",
                  },
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-4">
                    <div className={`p-2 rounded-lg ${item.color}`}>
                      {item.icon}
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">
                        {item.action}
                      </p>
                      <p className="text-gray-500 text-xs">{item.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};
